import React from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable } from 'mobx';
import {
    Alert, Button, Card, Checkbox, Form, Input, InputNumber, Popconfirm, Space, Spin, Table, Tag, Typography, notification,
} from 'antd';
import { GrantCreditsProxy } from '../services/catalogServiceAPI/grantCreditsProxy';
import { parseList } from '../utils/parseList';
import { ASSETS_BASE_URL, validateImageUrl } from '../utils/validateImageUrl';

const { TextArea } = Input;

@observer
export class GrantCredits extends React.Component {
    proxy = new GrantCreditsProxy();

    @observable isLoadingHistory = false;
    @observable isGranting = false;
    @observable history = [];
    @observable resolvePreview = null;
    @observable lastResult = null;

    @observable form = {
        targetUids: '',
        targetEmails: '',
        gems: 0,
        coins: 0,
        reason: '',
        notifyRecipients: true,
        dialogTitle: '',
        dialogDescription: '',
        dialogIconToken: '',
        dialogImageUrl: '',
        ctaLabel: '',
    };

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @action.bound setLoadingHistory = (v) => { this.isLoadingHistory = v; };
    @action.bound setGranting = (v) => { this.isGranting = v; };
    @action.bound setHistory = (v) => { this.history = v; };
    @action.bound setField = (k, v) => { this.form[k] = v; };
    @action.bound setResolvePreview = (v) => { this.resolvePreview = v; };
    @action.bound setLastResult = (v) => { this.lastResult = v; };

    componentDidMount() {
        this.fetchHistory();
    }

    @computed get parsedUids() { return parseList(this.form.targetUids); }
    @computed get parsedEmails() { return parseList(this.form.targetEmails); }

    @computed get hasRecipientInput() {
        return this.parsedUids.length > 0 || this.parsedEmails.length > 0;
    }

    @computed get amountsValid() {
        const g = Number(this.form.gems) || 0;
        const c = Number(this.form.coins) || 0;
        return Number.isInteger(g) && g >= 0 && Number.isInteger(c) && c >= 0 && g + c > 0;
    }

    @computed get reasonValid() { return this.form.reason.trim().length > 0; }

    @computed get dialogActive() {
        return this.form.dialogTitle.trim().length > 0 || this.form.dialogDescription.trim().length > 0;
    }

    @computed get dialogTitleError() {
        if (!this.dialogActive) return null;
        return this.form.dialogTitle.trim().length === 0 ? 'Required when description is set.' : null;
    }

    @computed get dialogDescriptionError() {
        if (!this.dialogActive) return null;
        return this.form.dialogDescription.trim().length === 0 ? 'Required when title is set.' : null;
    }

    @computed get imageUrlError() {
        return validateImageUrl(this.form.dialogImageUrl);
    }

    @computed get dialogValid() {
        if (!this.dialogActive) return true;
        return !this.dialogTitleError && !this.dialogDescriptionError && !this.imageUrlError;
    }

    @computed get canGrant() {
        return this.hasRecipientInput && this.amountsValid && this.reasonValid && this.dialogValid;
    }

    buildGrantPayload = () => {
        const payload = {
            targetUids: this.parsedUids,
            targetEmails: this.parsedEmails,
            gems: Number(this.form.gems) || 0,
            coins: Number(this.form.coins) || 0,
            reason: this.form.reason.trim(),
            notifyRecipients: !!this.form.notifyRecipients,
        };

        if (this.form.notifyRecipients && this.dialogActive && this.dialogValid) {
            payload.dialogCta = {
                title: this.form.dialogTitle.trim(),
                description: this.form.dialogDescription.trim(),
                iconToken: this.form.dialogIconToken.trim() || null,
                imageUrl: this.form.dialogImageUrl.trim() || null,
                label: this.form.ctaLabel.trim() || null,
            };
        }
        return payload;
    };

    async fetchHistory() {
        this.setLoadingHistory(true);
        try {
            const rows = await this.proxy.getHistory(50);
            this.setHistory(rows);
        } catch (e) {
            notification.error({ message: 'Failed to load history', description: e.message });
        } finally {
            this.setLoadingHistory(false);
        }
    }

    verify = async () => {
        if (!this.hasRecipientInput) {
            notification.warning({ message: 'Add at least one UID or email.' });
            return;
        }
        try {
            const result = await this.proxy.resolveTargets({
                targetUids: this.parsedUids,
                targetEmails: this.parsedEmails,
            });
            this.setResolvePreview(result);
        } catch (e) {
            notification.error({ message: 'Failed to resolve targets', description: e.message });
        }
    };

    grant = async () => {
        if (!this.canGrant) {
            notification.warning({ message: 'Form is not valid.' });
            return;
        }
        this.setGranting(true);
        try {
            const result = await this.proxy.grant(this.buildGrantPayload());
            this.setLastResult(result);
            const summary = `${result.granted} / ${result.totalRecipients} granted`
                + (this.form.notifyRecipients ? `, ${result.notified} notified` : '');
            if ((result.unresolvedEmails && result.unresolvedEmails.length > 0)
                || (result.failedUids && result.failedUids.length > 0)) {
                notification.warning({
                    message: summary,
                    description: [
                        result.unresolvedEmails?.length
                            ? `Unresolved emails: ${result.unresolvedEmails.join(', ')}`
                            : null,
                        result.failedUids?.length
                            ? `Failed UIDs: ${result.failedUids.join(', ')}`
                            : null,
                    ].filter(Boolean).join(' · '),
                });
            } else {
                notification.success({ message: summary });
            }
            await this.fetchHistory();
        } catch (e) {
            notification.error({ message: 'Failed to grant', description: e.message });
        } finally {
            this.setGranting(false);
        }
    };

    @computed get groupedHistory() {
        const bucketsByKey = new Map();
        for (const row of this.history) {
            const key = row.batchId
                ? `${row.batchId}|${row.userId}`
                : `${row.referenceId ?? 'tx-' + row.id}|${row.userId}`;
            const existing = bucketsByKey.get(key);
            if (!existing) {
                bucketsByKey.set(key, {
                    key,
                    createdAt: row.createdAt,
                    userId: row.userId,
                    userEmail: row.userEmail,
                    gems: row.bucket === 'permanent' ? row.delta : 0,
                    coins: row.bucket === 'coin' ? row.delta : 0,
                    reason: row.reason,
                    grantedBy: row.grantedBy,
                    grantedByEmail: row.grantedByEmail,
                });
            } else {
                if (row.bucket === 'permanent') existing.gems += row.delta;
                if (row.bucket === 'coin') existing.coins += row.delta;
                if (new Date(row.createdAt).getTime() < new Date(existing.createdAt).getTime()) {
                    existing.createdAt = row.createdAt;
                }
            }
        }
        return Array.from(bucketsByKey.values()).sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    }

    historyColumns = [
        {
            title: 'When',
            dataIndex: 'createdAt',
            width: 170,
            render: (v) => new Date(v).toLocaleString(),
        },
        {
            title: 'Recipient',
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text>{row.userEmail || <Typography.Text type="secondary">—</Typography.Text>}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.userId}</Typography.Text>
                </Space>
            ),
        },
        {
            title: 'Gems',
            dataIndex: 'gems',
            width: 90,
            render: (v) => v ? <Tag color="purple">{v >= 0 ? `+${v}` : v}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'Coins',
            dataIndex: 'coins',
            width: 90,
            render: (v) => v ? <Tag color="gold">{v >= 0 ? `+${v}` : v}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'Granted by',
            render: (_, row) => row.grantedByEmail || row.grantedBy || <Typography.Text type="secondary">—</Typography.Text>,
        },
    ];

    render() {
        const targetCount = this.resolvePreview?.uids?.length ?? null;
        const unresolvedCount = this.resolvePreview?.unresolvedEmails?.length ?? 0;

        return (
            <Space direction="vertical" size={16} style={{ width: '100%', padding: '24px 32px' }}>
                <Typography.Title level={3} style={{ margin: 0 }}>Grant Gems & Coins</Typography.Title>

                <Card>
                    <Form layout="vertical">
                        <Form.Item label="Target UIDs" help="One per line, comma- or space-separated.">
                            <TextArea
                                rows={3}
                                value={this.form.targetUids}
                                onChange={e => this.setField('targetUids', e.target.value)}
                                placeholder="abc123uid&#10;def456uid"
                            />
                        </Form.Item>
                        <Form.Item label="Target emails" help="Resolved server-side. Unmatched ones are reported back.">
                            <TextArea
                                rows={3}
                                value={this.form.targetEmails}
                                onChange={e => this.setField('targetEmails', e.target.value)}
                                placeholder="alice@example.com&#10;bob@example.com"
                            />
                        </Form.Item>
                        <Space size="large" wrap>
                            <Form.Item label="Gems" required>
                                <InputNumber
                                    min={0}
                                    step={1}
                                    value={this.form.gems}
                                    onChange={v => this.setField('gems', v ?? 0)}
                                    style={{ width: 140 }}
                                />
                            </Form.Item>
                            <Form.Item label="Coins" required>
                                <InputNumber
                                    min={0}
                                    step={1}
                                    value={this.form.coins}
                                    onChange={v => this.setField('coins', v ?? 0)}
                                    style={{ width: 140 }}
                                />
                            </Form.Item>
                        </Space>
                        {!this.amountsValid && (this.form.gems > 0 || this.form.coins > 0 ? null : (
                            <Typography.Text type="warning" style={{ display: 'block', marginTop: -8, marginBottom: 12 }}>
                                At least one of gems/coins must be greater than zero.
                            </Typography.Text>
                        ))}
                        <Form.Item
                            label="Reason"
                            required
                            help="Stored on the transaction's metadata for audit. Not shown to the recipient."
                            validateStatus={!this.reasonValid && this.form.reason.length > 0 ? 'error' : ''}
                        >
                            <Input
                                value={this.form.reason}
                                onChange={e => this.setField('reason', e.target.value)}
                                placeholder="e.g. Support refund for failed render #12345"
                                maxLength={300}
                                showCount
                            />
                        </Form.Item>
                        <Form.Item>
                            <Checkbox
                                checked={this.form.notifyRecipients}
                                onChange={e => this.setField('notifyRecipients', e.target.checked)}
                            >
                                Send a notification to recipients about this grant.
                            </Checkbox>
                        </Form.Item>

                        {this.form.notifyRecipients && (
                            <Card
                                size="small"
                                title="Pop a dialog when the user clicks the notification (optional; opens as the rich dialog campaigns use)"
                                style={{ marginBottom: 16 }}
                            >
                                <Form.Item
                                    label="Dialog title"
                                    help="Required when description is set."
                                    validateStatus={this.dialogTitleError ? 'error' : ''}
                                >
                                    <Input
                                        value={this.form.dialogTitle}
                                        onChange={e => this.setField('dialogTitle', e.target.value)}
                                        placeholder="e.g. Welcome back!"
                                        maxLength={120}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Dialog description"
                                    help="Required when title is set. Newlines are preserved."
                                    validateStatus={this.dialogDescriptionError ? 'error' : ''}
                                >
                                    <TextArea
                                        rows={3}
                                        value={this.form.dialogDescription}
                                        onChange={e => this.setField('dialogDescription', e.target.value)}
                                        placeholder="What you'd like the recipient to read."
                                        maxLength={1000}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Dialog icon (optional)"
                                    help="Name of an icon from common/icons (case-insensitive). Examples: Gift, Trophy, Crown, Heart, Coin."
                                >
                                    <Input
                                        value={this.form.dialogIconToken}
                                        onChange={e => this.setField('dialogIconToken', e.target.value)}
                                        placeholder="e.g. gift"
                                        style={{ width: 220 }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Dialog image URL (optional)"
                                    help={this.imageUrlError
                                        ? this.imageUrlError
                                        : `Must be hosted under ${ASSETS_BASE_URL}. Used instead of the icon when set.`}
                                    validateStatus={this.imageUrlError ? 'error' : ''}
                                >
                                    <Input
                                        value={this.form.dialogImageUrl}
                                        onChange={e => this.setField('dialogImageUrl', e.target.value)}
                                        placeholder={`${ASSETS_BASE_URL}/…`}
                                        status={this.imageUrlError ? 'error' : ''}
                                    />
                                </Form.Item>
                                <Form.Item label="CTA button label (optional)" help='Defaults to "Open" when blank.'>
                                    <Input
                                        value={this.form.ctaLabel}
                                        onChange={e => this.setField('ctaLabel', e.target.value)}
                                        placeholder="Open"
                                        maxLength={30}
                                        style={{ width: 220 }}
                                    />
                                </Form.Item>
                            </Card>
                        )}

                        <Space>
                            <Button onClick={this.verify}>Verify recipients</Button>
                            <Popconfirm
                                title={
                                    targetCount !== null
                                        ? `Grant ${this.form.gems} gems + ${this.form.coins} coins to ${targetCount} ${targetCount === 1 ? 'user' : 'users'}?`
                                        : `Grant ${this.form.gems} gems + ${this.form.coins} coins to listed recipients?`
                                }
                                description={this.form.notifyRecipients ? 'Recipients will be notified.' : 'Recipients will NOT be notified.'}
                                onConfirm={this.grant}
                                okText="Grant"
                                disabled={!this.canGrant}
                            >
                                <Button type="primary" loading={this.isGranting} disabled={!this.canGrant}>Grant</Button>
                            </Popconfirm>
                        </Space>

                        {this.resolvePreview && (
                            <Alert
                                style={{ marginTop: 16 }}
                                type={unresolvedCount > 0 ? 'warning' : 'info'}
                                showIcon
                                message={`${targetCount} resolved recipient${targetCount === 1 ? '' : 's'}`}
                                description={
                                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                        {targetCount > 0 && (
                                            <Typography.Paragraph
                                                copyable={{ text: this.resolvePreview.uids.join('\n'), tooltips: ['Copy all UIDs', 'Copied'] }}
                                                style={{ margin: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                                            >
                                                {this.resolvePreview.uids.join('\n')}
                                            </Typography.Paragraph>
                                        )}
                                        {unresolvedCount > 0 && (
                                            <Typography.Text type="danger">
                                                {unresolvedCount} email{unresolvedCount === 1 ? '' : 's'} couldn&apos;t be matched: {this.resolvePreview.unresolvedEmails.join(', ')}
                                            </Typography.Text>
                                        )}
                                    </Space>
                                }
                            />
                        )}
                    </Form>
                </Card>

                <Typography.Title level={4} style={{ margin: '8px 0 0' }}>
                    Recent grants {this.groupedHistory.length > 0 && <Tag>{this.groupedHistory.length}</Tag>}
                </Typography.Title>
                {this.isLoadingHistory
                    ? <Spin />
                    : (
                        <Table
                            rowKey="key"
                            dataSource={this.groupedHistory}
                            columns={this.historyColumns}
                            pagination={{ pageSize: 20 }}
                            size="middle"
                        />
                    )
                }
            </Space>
        );
    }
}
